import React, { useCallback, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { adminApi, formatUSD, formatLBP, arDate, fileUrl } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, KeyRound, X, Search, User as UserIcon, RefreshCw, CalendarPlus, DollarSign, Ban } from "lucide-react";
import SubscriptionBadge from "@/components/SubscriptionBadge";
import { canAdmin, useAdminAccess } from '@/context/AdminAccessContext';
const planLabel = type => ({ bank: 'اشتراك بنك العائلة', bank_amwali_plus: 'باقة أموالي +' })[type] || 'لا يوجد اشتراك فعال';

export default function UsersPage() {
  const navigate = useNavigate();
  const viewAs = async user => { try { const { data } = await adminApi.post(`/admin/users/${user.id}/view-as`); sessionStorage.setItem("mb_view_token", data.token); sessionStorage.setItem("mb_view_id", data.view_id); navigate(`/admin/view-user/${user.id}`); } catch(error) { toast.error(error.response?.data?.detail || "تعذر استعراض الحساب."); } };
  const access = useAdminAccess(); const can = permission => canAdmin(access, permission);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState('all');
  const [showCreate, setShowCreate] = useState(false);
  const [editing, setEditing] = useState(null);
  const [resetting, setResetting] = useState(null);
  const [viewing, setViewing] = useState(null);
  const [editMembership, setEditMembership] = useState(null);
  const [permanentDelete, setPermanentDelete] = useState(null);

  // New Admin Modals
  const [gamificationManager, setGamificationManager] = useState(null); // User for level/XP/badges
  const [balanceEdit, setBalanceEdit] = useState(null); // User for balance edits

  const refresh = useCallback(async () => {
    try {
      const { data } = await adminApi.get("/admin/users");
      setUsers(data);
    } catch {
      toast.error("تعذّر تحميل المستخدمين");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const filtered = users.filter((u) => {
    const matchesQuery = !q || u.username.toLowerCase().includes(q.toLowerCase()) || (u.display_name || "").toLowerCase().includes(q.toLowerCase());
    return matchesQuery && (statusFilter === 'all' || (statusFilter === 'active' ? !u.is_suspended : u.is_suspended));
  });

  const toggleSuspension = async (u) => {
    try {
      const res = await adminApi.post(`/admin/users/${u.id}/toggle-status`);
      toast.success(res.data.is_suspended ? "تم تعطيل حساب المستخدم" : "تم إعادة تفعيل الحساب");
      refresh();
    } catch (e) {
      toast.error("فشل تغيير حالة الحساب");
    }
  };

  return (
    <div className="space-y-6" data-testid="users-page">
      <header className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="text-xs text-[var(--c-gold)] tracking-[0.3em] uppercase font-semibold">إدارة الحسابات والاشتراكات</div>
          <h1 className="section-title text-3xl sm:text-4xl mt-2">مركز إدارة المستخدمين</h1>
          <p className="text-[var(--c-muted)] mt-2 text-sm">ملفات الحسابات، الوصول، الاشتراكات، والضوابط الإدارية من مكان واحد.</p>
        </div>
        {can('users.create') && <button onClick={() => setShowCreate(true)} className="btn-navy rounded-xl px-5 py-3 inline-flex items-center gap-2" data-testid="open-create-user">
          <Plus className="w-4 h-4" />
          <span className="text-sm font-bold">إضافة مستخدم جديد</span>
        </button>}
      </header>

      <section className="grid gap-3 sm:grid-cols-3" aria-label="ملخص المستخدمين">
        <div className="rounded-2xl border border-blue-100 bg-blue-50/70 p-4"><div className="text-xs text-blue-700">كل الحسابات</div><div className="mt-1 text-2xl font-black text-blue-950">{users.length}</div></div>
        <div className="rounded-2xl border border-emerald-100 bg-emerald-50/70 p-4"><div className="text-xs text-emerald-700">الحسابات النشطة</div><div className="mt-1 text-2xl font-black text-emerald-950">{users.filter(u => !u.is_suspended).length}</div></div>
        <div className="rounded-2xl border border-rose-100 bg-rose-50/70 p-4"><div className="text-xs text-rose-700">الحسابات المعطلة</div><div className="mt-1 text-2xl font-black text-rose-950">{users.filter(u => u.is_suspended).length}</div></div>
      </section>

      <div className="bg-white border border-[var(--c-border)] rounded-2xl p-4 sm:p-6">
        <div className="mb-5 flex flex-wrap gap-3">
        <div className="relative max-w-md flex-1 min-w-[14rem]">
          <Search className="w-4 h-4 absolute top-1/2 -translate-y-1/2 end-3 text-[var(--c-muted)]" />
          <input className="input-pro pe-9" placeholder="بحث باسم المستخدم أو الاسم الظاهر…" value={q} onChange={(e) => setQ(e.target.value)} data-testid="users-search" />
        </div>
        <select className="input-pro w-auto" value={statusFilter} onChange={event => setStatusFilter(event.target.value)} aria-label="تصفية حالة الحساب"><option value="all">كل الحالات</option><option value="active">نشط</option><option value="suspended">معطل</option></select>
        <button className="rounded-xl border px-3 text-xs font-bold" onClick={refresh} aria-label="تحديث القائمة"><RefreshCw className="h-4 w-4" /></button>
        </div>

        {loading ? (
          <div className="text-sm text-[var(--c-muted)]">جاري التحميل…</div>
        ) : filtered.length === 0 ? (
          <div className="text-sm text-[var(--c-muted)] py-10 text-center">لا يوجد مستخدمون.</div>
        ) : (
          <>
          <div className="hidden md:block mb-table-wrap">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-[var(--c-muted)] text-xs uppercase tracking-wider border-b border-[var(--c-border)]">
                  <th className="text-start font-medium p-3">المستخدم والمستوى</th>
                  <th className="text-start font-medium p-3">حالة الاشتراك / الانتهاء</th>
                  {can('users.balances.view') && <th className="text-start font-medium p-3">الرصيد المتاح</th>}
                  <th className="text-start font-medium p-3">الحالة</th>
                  <th className="text-end font-medium p-3">إجراءات التحكم المتقدمة</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((u) => (
                  <tr key={u.id} className="border-b border-[var(--c-border)] last:border-0 hover:bg-[var(--c-cream)]/60 transition-colors" data-testid={`user-row-${u.username}`}>
                    <td className="p-3">
                      <button onClick={() => setViewing(u)} className="flex items-center gap-3 text-start group">
                        <div className="w-10 h-10 rounded-full bg-[var(--c-navy)]/8 border border-[var(--c-navy)]/15 flex items-center justify-center overflow-hidden shrink-0">
                          {u.avatar_path ? (
                            <img src={fileUrl(u.avatar_path)} alt="" className="w-full h-full object-cover" />
                          ) : (
                            <UserIcon className="w-4 h-4 text-[var(--c-navy)]" />
                          )}
                        </div>
                        <div>
                          <div className="font-bold text-[var(--c-navy)] group-hover:text-[var(--c-gold)] transition-colors">{u.display_name}</div>
                          <div className="text-xs text-[var(--c-muted)]">
                            @{u.username} · {u.level_info?.title_ar || "مدخر مبتدئ"} ({u.financial_profile?.xp || 0} XP)
                          </div>
                        </div>
                      </button>
                    </td>
                    <td className="p-3">
                      <div className="flex flex-col gap-1 items-start">
                        <SubscriptionBadge sub={u.subscription} showDays />
                        {u.subscription?.end && (
                          <div className="text-[10px] text-[var(--c-muted)] font-num">ينتهي في: {u.subscription.end}</div>
                        )}
                      </div>
                    </td>
                    {can('users.balances.view') && <td className="p-3">
                      <div className="font-num text-[var(--c-navy)] text-xs font-bold">$ {formatUSD(u.balance_usd)}</div>
                      <div className="font-num text-[var(--c-muted)] text-[11px]">{formatLBP(u.balance_lbp)} ل.ل</div>
                    </td>}
                    <td className="p-3">
                      <button
                        onClick={() => can('users.status.manage') && toggleSuspension(u)}
                        disabled={!can('users.status.manage')}
                        className={`text-xs px-2.5 py-1 rounded-full font-bold border transition ${
                          u.is_suspended
                            ? 'bg-red-50 text-red-600 border-red-200 hover:bg-red-100'
                            : 'bg-emerald-50 text-emerald-600 border-emerald-200 hover:bg-emerald-100'
                        }`}
                      >
                        {u.is_suspended ? 'معطل' : 'نشط'}
                      </button>
                    </td>
                    <td className="p-3">
                      <div className="flex items-center justify-end gap-1.5 flex-wrap">
                        {can('users.view') && <ActionBtn onClick={() => viewAs(u)} icon={UserIcon} label="الدخول إلى حساب المستخدم" testid={`view-as-${u.username}`} />}
                        {can('users.view') && <ActionBtn onClick={() => setViewing(u)} icon={UserIcon} label="عرض الحساب" testid={`view-${u.username}`} />}
                        {can('users.subscriptions.manage') && <ActionBtn onClick={() => navigate(`/admin/dashboard/subscriptions?user=${encodeURIComponent(u.id)}`)} icon={CalendarPlus} label="إدارة الاشتراك" testid={`sub-mgr-${u.username}`} />}
                        {can('users.security.manage') && <ActionBtn onClick={() => setResetting(u)} icon={KeyRound} label="تعيين كلمة مرور" testid={`reset-${u.username}`} />}
                        {(can('users.edit') || can('users.balances.modify') || can('users.status.manage') || can('users.delete.permanent')) && <details className="relative"><summary className="cursor-pointer list-none rounded-lg border border-[var(--c-border)] px-3 py-2 text-xs font-bold text-[var(--c-navy)]">إجراءات أخرى</summary><div className="absolute end-0 z-20 mt-1 w-52 rounded-xl border border-[var(--c-border)] bg-white p-1 shadow-xl">{can('users.edit') && <ActionBtn onClick={() => setEditing(u)} icon={Pencil} label="تعديل المستخدم" />}{can('users.balances.modify') && <ActionBtn onClick={() => setBalanceEdit(u)} icon={DollarSign} label="تعديل الرصيد" />}{can('users.status.manage') && <ActionBtn onClick={() => toggleSuspension(u)} icon={Ban} label={u.is_suspended ? 'إعادة تفعيل الحساب' : 'تعطيل الحساب'} />}{can('users.delete.permanent') && <ActionBtn onClick={() => setPermanentDelete(u)} icon={Trash2} label="حذف نهائي" danger />}</div></details>}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="md:hidden space-y-3">
            {filtered.map((u) => (
              <article key={u.id} className="rounded-2xl border border-[var(--c-border)] bg-[var(--color-surface)] p-4 shadow-sm" data-testid={`user-card-${u.username}`}>
                <div className="flex items-start justify-between gap-3">
                  <button onClick={() => can('users.view') && setViewing(u)} className="min-w-0 flex items-center gap-3 text-start" disabled={!can('users.view')}>
                    <div className="w-11 h-11 rounded-full bg-[var(--c-navy)]/8 border border-[var(--c-navy)]/15 flex items-center justify-center overflow-hidden shrink-0">
                      {u.avatar_path ? <img src={fileUrl(u.avatar_path)} alt="" className="w-full h-full object-cover" /> : <UserIcon className="w-4 h-4 text-[var(--c-navy)]" />}
                    </div>
                    <div className="min-w-0"><div className="font-bold text-[var(--c-navy)] break-words">{u.display_name}</div><div className="mt-1 text-xs text-[var(--c-muted)] break-all">@{u.username}</div></div>
                  </button>
                  <button onClick={() => can('users.status.manage') && toggleSuspension(u)} disabled={!can('users.status.manage')} className={`shrink-0 text-xs px-2.5 py-1 rounded-full font-bold border disabled:opacity-70 ${u.is_suspended ? 'bg-red-50 text-red-600 border-red-200' : 'bg-emerald-50 text-emerald-600 border-emerald-200'}`}>{u.is_suspended ? 'معطل' : 'نشط'}</button>
                </div>
                <div className="mt-4 grid grid-cols-2 gap-3 rounded-xl bg-[var(--c-cream)]/60 p-3">
                  <div className="min-w-0"><div className="text-[10px] text-[var(--c-muted)]">الاشتراك</div><div className="mt-1"><SubscriptionBadge sub={u.subscription} showDays /></div></div>
                  <div className="min-w-0 text-end"><div className="text-[10px] text-[var(--c-muted)]">الرصيد</div><div className="mt-1 font-num text-xs font-black text-[var(--c-navy)] break-all">$ {formatUSD(u.balance_usd)}</div><div className="font-num text-[10px] text-[var(--c-muted)] break-all">{formatLBP(u.balance_lbp)} ل.ل</div></div>
                </div>
                <div className="mt-3 grid grid-cols-2 gap-2">
                  {can('users.view') && <ActionBtn onClick={() => viewAs(u)} icon={UserIcon} label="الدخول إلى حساب المستخدم" testid={`mobile-view-as-${u.username}`} />}
                  {can('users.view') && <ActionBtn onClick={() => setViewing(u)} icon={UserIcon} label="عرض الحساب" />}
                  {can('users.subscriptions.manage') && <ActionBtn onClick={() => navigate(`/admin/dashboard/subscriptions?user=${encodeURIComponent(u.id)}`)} icon={CalendarPlus} label="إدارة الاشتراك" testid={`mobile-sub-mgr-${u.username}`} />}
                  {can('users.security.manage') && <ActionBtn onClick={() => setResetting(u)} icon={KeyRound} label="تعيين كلمة مرور" testid={`mobile-reset-${u.username}`} />}
                  {can('users.delete.permanent') && <ActionBtn onClick={() => setPermanentDelete(u)} icon={Trash2} label="حذف نهائي" danger />}
                </div>
              </article>
            ))}
          </div>
          </>
        )}
      </div>

      {showCreate && <CreateUserModal onClose={() => setShowCreate(false)} onSaved={() => { setShowCreate(false); refresh(); }} />}
      {gamificationManager && <GamificationManagerModal user={gamificationManager} onClose={() => setGamificationManager(null)} onSaved={() => { setGamificationManager(null); refresh(); }} />}
      {balanceEdit && <BalanceEditModal user={balanceEdit} onClose={() => setBalanceEdit(null)} onSaved={() => { setBalanceEdit(null); refresh(); }} />}
      {editing && <EditUsernameModal user={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); refresh(); }} />}
      {editMembership && <EditMembershipModal user={editMembership} onClose={() => setEditMembership(null)} onSaved={() => { setEditMembership(null); refresh(); }} />}
      {resetting && <ResetPasswordModal user={resetting} onClose={() => setResetting(null)} onSaved={() => { setResetting(null); refresh(); }} />}
      {permanentDelete && <PermanentDeleteModal user={permanentDelete} onClose={() => setPermanentDelete(null)} onSaved={() => { setPermanentDelete(null); refresh(); }} />}
      {viewing && <UserDetailModal user={viewing} onClose={() => setViewing(null)} />}
    </div>
  );
}

function IconBtn({ children, onClick, title, danger, testid }) {
  return (
    <button
      onClick={onClick}
      title={title}
      data-testid={testid}
      aria-label={title}
      className={`w-10 h-10 rounded-lg border flex items-center justify-center transition-all
        ${danger ? "border-[var(--c-danger)]/20 text-[var(--c-danger)] hover:bg-[var(--c-danger)] hover:text-white" :
                   "border-[var(--c-border)] text-[var(--c-navy)] hover:border-[var(--c-gold)]/50 hover:text-[var(--c-gold)]"}`}
    >
      {children}
    </button>
  );
}

function ActionBtn({ onClick, icon: Icon, label, danger, testid }) {
  return <button onClick={onClick} data-testid={testid} className={`flex min-h-9 items-center gap-1.5 rounded-lg px-2.5 py-2 text-xs font-bold transition ${danger ? 'text-red-700 hover:bg-red-50' : 'text-[var(--c-navy)] hover:bg-[var(--c-cream)]'}`}><Icon className="h-3.5 w-3.5" />{label}</button>;
}

function Modal({ title, onClose, children, testid, portal = false }) {
  const content = (
    <div className={`${portal ? 'family-auth-shell' : ''} fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm fade-up`} onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className="w-full max-w-lg max-h-[calc(100dvh-2rem)] overflow-y-auto bg-white rounded-2xl border border-[var(--c-border)] shadow-2xl p-5 sm:p-7 space-y-4" data-testid={testid} role="dialog" aria-modal="true">
        <div className="flex items-center justify-between border-b border-[var(--c-border)] pb-3">
          <h3 className="section-title text-xl">{title}</h3>
          <button onClick={onClose} className="text-[var(--c-muted)] hover:text-[var(--c-navy)]" data-testid="modal-close">
            <X className="w-5 h-5" />
          </button>
        </div>
        {children}
      </div>
    </div>
  );
  return portal ? createPortal(content, document.body) : content;
}

function CreateUserModal({ onClose, onSaved }) {
  const [username, setUsername] = useState("");
  const [display, setDisplay] = useState("");
  const [membership, setMembership] = useState("");
  const [subType, setSubType] = useState("unsubscribed");
  const [subStart, setSubStart] = useState(new Date().toISOString().slice(0, 10));
  const [usd, setUsd] = useState(0);
  const [lbp, setLbp] = useState(0);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!username.trim()) return toast.error("اسم المستخدم مطلوب");
    setLoading(true);
    try {
      await adminApi.post("/admin/users", {
        username: username.trim(),
        display_name: display.trim() || undefined,
        membership_number: membership.trim() || undefined,
        subscription_type: subType,
        subscription_start: subStart,
        initial_usd: Number(usd) || 0,
        initial_lbp: Number(lbp) || 0,
      });
      toast.success("تم إنشاء حساب المستخدم بنجاح");
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "فشل الإنشاء");
    } finally { setLoading(false); }
  };

  return (
    <Modal title="إضافة حساب مستخدم جديد" onClose={onClose} testid="create-user-modal">
      <form onSubmit={submit} className="space-y-4">
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <div>
            <label className="label-pro">اسم المستخدم (Login Username)</label>
            <input className="input-pro" value={username} onChange={(e) => setUsername(e.target.value)} autoFocus />
          </div>
          <div>
            <label className="label-pro">الاسم الظاهر (Display Name)</label>
            <input className="input-pro" value={display} onChange={(e) => setDisplay(e.target.value)} />
          </div>
        </div>

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          <div>
            <label className="label-pro">نوع الاشتراك</label>
            <select className="input-pro font-bold" value={subType} onChange={(e) => setSubType(e.target.value)}>
              <option value="bank">بنك العائلة</option>
              <option value="bank_amwali_plus">باقة أموالي +</option>
              <option value="unsubscribed">بدون اشتراك</option>
            </select>
          </div>
          <div>
            <label className="label-pro">مدة الاشتراك</label>
            <div className="input-pro">شهر واحد</div>
          </div>
          <div>
            <label className="label-pro">تاريخ بداية الاشتراك</label>
            <input type="date" className="input-pro" disabled={subType === 'unsubscribed'} value={subStart} onChange={(e) => setSubStart(e.target.value)} />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3 border-t border-[var(--c-border)] pt-3">
          <div>
            <label className="label-pro">رصيدUSD مفتتح</label>
            <input type="number" step="0.01" className="input-pro font-num" value={usd} onChange={(e) => setUsd(e.target.value)} />
          </div>
          <div>
            <label className="label-pro">رصيدLBP مفتتح</label>
            <input type="number" step="5000" className="input-pro font-num" value={lbp} onChange={(e) => setLbp(e.target.value)} />
          </div>
        </div>

        <button disabled={loading} className="btn-navy w-full rounded-xl py-3 font-bold">
          {loading ? "جارٍ الإنشاء..." : "إنشاء المستخدم"}
        </button>
      </form>
    </Modal>
  );
}

// Gamification Manager Modal
function GamificationManagerModal({ user, onClose, onSaved }) {
  const profile = user.financial_profile || {};
  const [xpDelta, setXpDelta] = useState(50);
  const [reason, setReason] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await adminApi.patch(`/admin/users/${user.id}/gamification`, {
        xp_delta: Number(xpDelta),
        reason
      });
      toast.success("تم تعديل XP وتسجيل السبب في السجل.");
      onSaved();
    } catch (e) {
      toast.error("فشل التعديل");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal title={`إدارة نقاط XP والمستوى — @${user.username}`} onClose={onClose}>
      <form onSubmit={submit} className="space-y-4">
        <div className="bg-[var(--c-cream)] p-4 rounded-xl border border-[var(--c-border)] text-xs space-y-1">
          <div>النقاط الحالية: <span className="font-bold font-num text-[var(--c-gold)]">{profile.xp || 0} XP</span></div>
          <div>المستوى الحالي: <span className="font-bold text-[var(--c-navy)]">المستوى {profile.level || 1}</span></div>
        </div>

        <div>
          <label className="label-pro">إضافة نقاط XP إضافية</label>
          <input type="number" className="input-pro font-num" value={xpDelta} onChange={(e) => setXpDelta(e.target.value)} />
        </div>

        <div>
          <label className="label-pro">سبب التعديل <span className="text-red-600">*</span></label>
          <textarea required className="input-pro" value={reason} onChange={(e) => setReason(e.target.value)} />
        </div>

        <button disabled={loading} className="btn-navy w-full rounded-xl py-3 font-bold">
          حفظ النقاط والمستوى
        </button>
      </form>
    </Modal>
  );
}

// Balance Edit Modal
function BalanceEditModal({ user, onClose, onSaved }) {
  const [usd, setUsd] = useState(user.balance_usd || 0);
  const [lbp, setLbp] = useState(user.balance_lbp || 0);
  const [note, setNote] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await adminApi.patch(`/admin/users/${user.id}/balance`, {
        balance_usd: Number(usd),
        balance_lbp: Number(lbp),
        note
      });
      toast.success("تم تعديل الرصيد بنجاح");
      onSaved();
    } catch (e) {
      toast.error("فشل التعديل");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal title={`تعديل الرصيد المباشر — @${user.username}`} onClose={onClose}>
      <form onSubmit={submit} className="space-y-4">
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="label-pro">الرصيدUSD ($)</label>
            <input type="number" step="0.01" className="input-pro font-num" value={usd} onChange={(e) => setUsd(e.target.value)} />
          </div>
          <div>
            <label className="label-pro">الرصيدLBP (ل.ل)</label>
            <input type="number" step="5000" className="input-pro font-num" value={lbp} onChange={(e) => setLbp(e.target.value)} />
          </div>
        </div>
        <div>
          <label className="label-pro">سبب التعديل / ملاحظات</label>
          <input className="input-pro" placeholder="تعديل إداري مالي" value={note} onChange={(e) => setNote(e.target.value)} />
        </div>
        <button disabled={loading} className="btn-navy w-full rounded-xl py-3 font-bold">
          حفظ الرصيد الجديد
        </button>
      </form>
    </Modal>
  );
}

function EditMembershipModal({ user, onClose, onSaved }) {
  const [v, setV] = useState(user.membership_number || "");
  const [loading, setLoading] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await adminApi.patch(`/admin/users/${user.id}/membership`, { membership_number: v.trim() || null });
      toast.success("تم التحديث");
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "فشل التحديث");
    } finally { setLoading(false); }
  };
  return (
    <Modal title={`رقم العضوية — @${user.username}`} onClose={onClose} testid="membership-modal">
      <form onSubmit={submit} className="space-y-4">
        <div><label className="label-pro">رقم العضوية (اتركه فارغاً للإزالة)</label>
          <input className="input-pro font-num" value={v} onChange={(e) => setV(e.target.value)} autoFocus data-testid="membership-input" /></div>
        <button disabled={loading} className="btn-navy w-full rounded-xl py-3 font-bold" data-testid="submit-membership">حفظ</button>
      </form>
    </Modal>
  );
}

function EditUsernameModal({ user, onClose, onSaved }) {
  const [v, setV] = useState(user.username);
  const [loading, setLoading] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await adminApi.patch(`/admin/users/${user.id}/username`, { username: v.trim() });
      toast.success("تم تعديل اسم المستخدم");
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "فشل التعديل");
    } finally { setLoading(false); }
  };
  return (
    <Modal title="تعديل اسم المستخدم" onClose={onClose}>
      <form onSubmit={submit} className="space-y-4">
        <div><label className="label-pro">اسم المستخدم الجديد</label>
          <input className="input-pro" value={v} onChange={(e) => setV(e.target.value)} data-testid="edit-username-input" autoFocus /></div>
        <button disabled={loading} className="btn-navy w-full rounded-xl py-3 font-bold" data-testid="submit-edit-username">حفظ</button>
      </form>
    </Modal>
  );
}

function ResetPasswordModal({ user, onClose, onSaved }) {
  const [mode, setMode] = useState('temporary');
  const [pw, setPw] = useState("");
  const [expiresAt, setExpiresAt] = useState(() => new Date(Date.now() + 60 * 60 * 1000).toISOString().slice(0, 16));
  const [generated, setGenerated] = useState("");
  const [loading, setLoading] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (mode === 'require') {
        await adminApi.post(`/admin/users/${user.id}/password/require-change`);
        toast.success('سيُطلب من المستخدم اختيار كلمة مرور جديدة عند الدخول التالي.'); onSaved(); return;
      }
      const endpoint = mode === 'permanent' ? 'permanent' : 'temporary';
      const payload = mode === 'permanent' ? { new_password: pw } : { temporary_password: pw || undefined, expires_at: new Date(expiresAt).toISOString() };
      const { data } = await adminApi.post(`/admin/users/${user.id}/password/${endpoint}`, payload);
      if (data.temporary_password) {
        setGenerated(data.temporary_password);
        toast.success("تم إنشاء كلمة مرور مؤقتة؛ ستظهر مرة واحدة فقط");
      } else {
        toast.success(mode === 'permanent' ? 'تم تعيين كلمة المرور الدائمة.' : 'تم تعيين كلمة المرور المؤقتة.');
        onSaved();
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "فشلت العملية");
    } finally { setLoading(false); }
  };
  return (
    <Modal title={`إدارة كلمة المرور — @${user.username}`} onClose={onClose}>
      <form onSubmit={submit} className="space-y-4">
        <p className="rounded-xl border border-slate-200 bg-slate-50 p-3 text-xs text-slate-600">كلمات المرور الحالية وتجزئاتها لا تظهر أبداً. كلمة المرور المؤقتة تحفظ كتجزئة منفصلة ولا تستبدل الدائمة.</p>
        <div className="grid grid-cols-3 gap-2 text-xs"><button type="button" onClick={()=>{setMode('permanent');setGenerated('')}} className={`rounded-xl border p-2 font-bold ${mode==='permanent'?'bg-[var(--c-navy)] text-white':''}`}>كلمة دائمة</button><button type="button" onClick={()=>{setMode('temporary');setGenerated('')}} className={`rounded-xl border p-2 font-bold ${mode==='temporary'?'bg-[var(--c-navy)] text-white':''}`}>كلمة مؤقتة</button><button type="button" onClick={()=>{setMode('require');setGenerated('')}} className={`rounded-xl border p-2 font-bold ${mode==='require'?'bg-[var(--c-navy)] text-white':''}`}>إلزام بالتغيير</button></div>
        {generated ? <div className="rounded-xl border border-amber-300 bg-amber-50 p-4"><div className="text-xs">انسخ كلمة المرور المؤقتة الآن؛ لن يمكن عرضها لاحقاً:</div><code className="block mt-2 text-lg select-all" dir="ltr">{generated}</code></div> : <>
          {mode === 'require' ? <p className="rounded-xl border border-amber-200 bg-amber-50 p-3 text-xs">سيستطيع المستخدم الدخول بكلمته الدائمة الحالية، ثم لا يمكنه الوصول للحساب حتى يختار كلمة جديدة بنفسه.</p> : <><div><label className="label-pro">{mode === 'permanent' ? 'كلمة مرور دائمة جديدة' : 'كلمة مرور مؤقتة (اتركها فارغة للتوليد الآمن)'}</label><input required={mode === 'permanent'} minLength={4} type="password" className="input-pro" value={pw} onChange={(e) => setPw(e.target.value)} data-testid="reset-password-input" /></div>{mode === 'temporary' && <div><label className="label-pro">تنتهي في</label><input required type="datetime-local" className="input-pro" value={expiresAt} onChange={e=>setExpiresAt(e.target.value)} /></div>}</>}
        </>}
        {generated ? <button type="button" onClick={onSaved} className="btn-navy w-full rounded-xl py-3 font-bold">تم النسخ والإغلاق</button> : <button disabled={loading} className="btn-navy w-full rounded-xl py-3 font-bold" data-testid="submit-reset-password">{mode === 'require' ? 'إلزام المستخدم بالتغيير' : 'تأكيد'}</button>}
      </form>
    </Modal>
  );
}

function PermanentDeleteModal({ user, onClose, onSaved }) {
  const [confirmation, setConfirmation] = useState('');
  const [loading, setLoading] = useState(false);
  const submit = async event => {
    event.preventDefault();
    setLoading(true);
    try {
      await adminApi.delete(`/admin/users/${user.id}/permanent`, { data: { confirm_username: confirmation.trim() } });
      toast.success('حُذف الحساب نهائياً وأُخفيت هويته من السجل المالي المحفوظ.');
      onSaved();
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'تعذر الحذف النهائي.');
    } finally { setLoading(false); }
  };
  return <Modal title={`حذف نهائي — @${user.username}`} onClose={onClose} testid="permanent-delete-modal">
    <form onSubmit={submit} className="space-y-4">
      <div className="rounded-xl border border-red-200 bg-red-50 p-4 text-sm leading-6 text-red-900"><b>لا يمكن التراجع عن هذا الإجراء.</b><br/>سيُحذف الحساب وكلمات المرور والمحتوى والصلاحيات. يبقى دفتر المعاملات فقط كـ «حساب محذوف» بلا بيانات شخصية.</div>
      <p className="text-xs text-[var(--c-muted)]">يشترط أن يكون الرصيد صفراً، ولا توجد صناديق نشطة أو طلبات موافقة معلّقة.</p>
      <label className="block"><span className="label-pro">اكتب اسم المستخدم للتأكيد: <b dir="ltr">{user.username}</b></span><input autoFocus className="input-pro mt-1" value={confirmation} onChange={event => setConfirmation(event.target.value)} data-testid="permanent-delete-confirmation" /></label>
      <button disabled={loading || confirmation.trim() !== user.username} className="w-full rounded-xl bg-red-700 py-3 text-sm font-black text-white hover:bg-red-800 disabled:cursor-not-allowed disabled:opacity-50" data-testid="submit-permanent-delete">{loading ? 'جارٍ الحذف…' : 'حذف الحساب نهائياً'}</button>
    </form>
  </Modal>;
}

function UserDetailModal({ user, onClose }) {
  const [detail, setDetail] = useState(null);
  const [tab, setTab] = useState('overview');
  useEffect(() => {
    (async () => {
      try {
        const { data } = await adminApi.get(`/admin/users/${user.id}`);
        setDetail(data);
      } catch { toast.error("تعذّر التحميل"); }
    })();
  }, [user.id]);
  return (
    <Modal portal title={`الملف التفصيلي — @${user.username}`} onClose={onClose}>
      {!detail ? <div className="text-sm text-[var(--c-muted)]">جاري التحميل…</div> : (
        <div className="space-y-4">
          <div className="flex gap-2 overflow-x-auto pb-1 text-xs whitespace-nowrap">
            {[['overview','الهوية'],['money','حساب البنك'],['outside','خارج البنك'],['subscription','الاشتراك'],['security','كلمة المرور'],['game','Gamification'],['goals','الأهداف'],['approvals','الموافقات'],['notifications','الإشعارات'],['activity','النشاط']].map(([id,label])=><button key={id} onClick={()=>setTab(id)} className={`px-3 py-2 rounded-xl ${tab===id?'bg-[var(--c-navy)] text-white':'bg-slate-100 text-slate-600'}`}>{label}</button>)}
          </div>
          {tab === 'overview' && <>
          <div className="grid grid-cols-2 gap-2 text-xs"><div className="rounded-xl bg-slate-50 p-3">اسم المستخدم: <b>@{detail.user.username}</b></div><div className="rounded-xl bg-slate-50 p-3">تاريخ الانضمام: <b>{detail.user.created_at ? arDate(detail.user.created_at) : '—'}</b></div><div className="rounded-xl bg-slate-50 p-3">رقم العضوية: <b>{detail.user.membership_number || '—'}</b></div><div className="rounded-xl bg-slate-50 p-3">حالة الحساب: <b>{detail.user.is_suspended?'مجمّد':'نشط'}</b></div></div>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div className="kpi-card !p-4"><div className="text-xs text-[var(--c-muted)]">الدولار المتاح</div>
              <div className="font-num text-xl sm:text-lg font-black text-[var(--c-gold)] mt-1">$ {formatUSD(detail.user.balance_usd)}</div></div>
            <div className="kpi-card !p-4"><div className="text-xs text-[var(--c-muted)]">الليرة المتاحة</div>
              <div className="font-num text-xl sm:text-lg font-black text-[var(--c-navy)] mt-1">{formatLBP(detail.user.balance_lbp)} ل.ل</div></div>
          </div>
          <div>
            <div className="text-xs text-[var(--c-muted)] mb-2">سجل العمليات</div>
            <ul className="max-h-60 overflow-auto divide-y divide-[var(--c-border)] border border-[var(--c-border)] rounded-xl">
              {detail.transactions.length === 0 ? <li className="p-3 text-sm text-[var(--c-muted)]">لا توجد عمليات.</li> :
                detail.transactions.slice(0, 30).map((t) => (
                  <li key={t.id} className="p-3 text-sm flex items-center justify-between">
                    <div><div className="font-medium text-[var(--c-navy)]">{txTypeAr(t)}</div>
                      <div className="text-[10px] text-[var(--c-muted)]">{arDate(t.created_at)}</div></div>
                    <div className="font-num font-bold text-[var(--c-navy)]">
                      {t.currency === "USD" ? `$ ${formatUSD(t.amount)}` : `${formatLBP(t.amount)} ل.ل`}
                    </div>
                  </li>
              ))}
            </ul>
          </div>
          <div className="grid grid-cols-2 gap-2 text-xs"><div className="p-3 rounded-xl bg-slate-50">الحالة: <b>{detail.user.is_suspended?'مجمّد':'نشط'}</b></div><div className="p-3 rounded-xl bg-slate-50">الخطة الفعالة: <b>{planLabel(detail.user.subscription?.type)}</b></div></div>
          </>}
          {tab === 'money' && <DetailList title="سجل العمليات" items={detail.transactions} render={t=><>{txTypeAr(t)} — {t.currency === "USD" ? `$ ${formatUSD(t.amount)}` : `${formatLBP(t.amount)} ل.ل`}</>} />}
          {tab === 'outside' && <DetailList title="رصيدا الأموال خارج البنك" items={detail.outside_money?.wallets || []} render={w=><>{w.name}: {w.current_balance} {w.currency}</>} />}
          {tab === 'subscription' && <div className="p-4 bg-slate-50 rounded-xl text-sm"><div>الخطة الفعالة: <b>{planLabel(detail.user.subscription?.type)}</b></div><div>الحالة: <b>{detail.user.subscription?.state === 'active' ? 'فعال' : 'لا يوجد اشتراك فعال'}</b></div><div>البداية: {detail.user.subscription?.start || '—'}</div><div>النهاية: {detail.user.subscription?.end || '—'}</div></div>}
          {tab === 'security' && <div className="space-y-2 rounded-xl bg-slate-50 p-4 text-sm"><div>كلمة دائمة: <b>{detail.password_security?.has_permanent_password ? 'موجودة' : 'غير معيّنة'}</b></div><div>إلزام بالتغيير: <b>{detail.password_security?.must_change_password ? 'نعم' : 'لا'}</b></div><div>صلاحية المؤقتة: <b>{detail.password_security?.temporary_password_expires_at ? arDate(detail.password_security.temporary_password_expires_at) : 'لا توجد'}</b></div><p className="pt-2 text-xs text-[var(--c-muted)]">لا يعرض النظام كلمات المرور أو تجزئاتها.</p></div>}
          {tab === 'game' && <><DetailList title="الأوسمة" items={detail.gamification?.financial_profile?.badges || []} render={b=><>{b}</>} /><DetailList title="التحديات" items={detail.gamification?.challenges || []} render={c=><>{c.title || c.title_ar} — {c.is_claimed?'تم الاستلام':'قيد التنفيذ'}</>} /></>}
          {tab === 'goals' && <DetailList title="الأهداف" items={detail.goals || []} render={g=><>{g.name || g.title}</>} />}
          {tab === 'approvals' && <DetailList title="طلبات الموافقات" items={detail.approvals} render={a=><>{a.type} — {a.amount} {a.currency} — {a.status}</>} />}
          {tab === 'notifications' && <DetailList title="الإشعارات" items={detail.notifications} render={n=><>{n.message}</>} />}
          {tab === 'activity' && <DetailList title="سجل النشاط" items={detail.activity} render={a=><>{a.action} — {a.created_at || a.timestamp}</>} />}
        </div>
      )}
    </Modal>
  );
}

function DetailList({ title, items = [], render }) {
  return <div><div className="text-xs text-[var(--c-muted)] mb-2">{title}</div><ul className="max-h-60 overflow-auto divide-y divide-[var(--c-border)] border border-[var(--c-border)] rounded-xl">{items.length===0?<li className="p-3 text-sm text-[var(--c-muted)]">لا توجد بيانات.</li>:items.slice(0,50).map((item,i)=><li key={item?.id || i} className="p-3 text-sm">{render(item)}</li>)}</ul></div>;
}

function txTypeAr(t) {
  if (t.type === "deposit") return "إيداع";
  if (t.type === "withdraw") return "سحب";
  if (t.type === "transfer") return t.direction === "out" ? `تحويل إلى @${t.counterparty_username}` : `تحويل من @${t.counterparty_username}`;
  return t.type;
}
